import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRealtime } from "@/lib/realtime";
import { Loader2, Plus, X, Trash2, RefreshCw, Link as LinkIcon, Filter,
  Bus, Calendar, ArrowRight, AlertCircle, AlertTriangle, CheckCircle2, Radio, ShieldCheck,
  Info, Sparkles, History, User as UserIcon, Zap, FileDown,
} from "lucide-react";

/**
 * Affectations flotte (Flotte → Affectations)
 * ────────────────────────────────────────────
 * Modèle hybride:
 *   • durable  — bus ↔ tournée pour l'année scolaire entière
 *   • override — exception ponctuelle sur une plage de dates
 *
 * Endpoints:
 *   GET    /api/tours                  → tournées importées + assignment durable courant
 *   GET    /api/tour-assignments       → liste affectations (filtrable)
 *   POST   /api/tour-assignments       → créer (durable | override)
 *   PATCH  /api/tour-assignments/{id}  → modifier (kind, dates, véhicule, chauffeur, statut)
 *   DELETE /api/tour-assignments/{id}  → supprimer
 */
export default function FleetAssignments() {
  const [tours, setTours] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfScope, setPdfScope] = useState("bus");
  const [pdfTarget, setPdfTarget] = useState("");
  const [pdfPeriod, setPdfPeriod] = useState("week");
  const [pdfLayout, setPdfLayout] = useState(() => localStorage.getItem("logibus_pdf_layout") || "condensed");
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("all");
  const [filterTour, setFilterTour] = useState("");
  const [dialog, setDialog] = useState(null); // { tour, kind } or null
  const [activeTab, setActiveTab] = useState("synthesis"); // "synthesis" | "weekly" | "buses" | "tours"

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, v, d] = await Promise.all([
        api.get("/tours"),
        api.get("/tour-assignments?include_inactive=false"),
        api.get("/vehicles"),
        api.get("/users?role=driver&active_only=true").catch(() => ({ data: [] })),
      ]);
      setTours(t.data?.items || []);
      setAssignments(a.data?.items || []);
      api.get("/tour-assignments/conflicts").then((c) => setConflicts(c.data?.items || [])).catch(() => {});
      const vList = Array.isArray(v.data) ? v.data : v.data?.items || [];
      setVehicles(vList);
      const dList = Array.isArray(d.data) ? d.data : d.data?.items || [];
      setDrivers(dList);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link support: when other pages (Planning Bus / Exploitation) need
  // to redirect the user here to edit an assignment, they navigate to
  // /admin/fleet-assignments?focus=<assignment_id> or
  // /admin/fleet-assignments?focus_tour=<tour_id>&kind=durable. We pop the
  // edit dialog open as soon as we have the data loaded.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    const focusTour = params.get("focus_tour");
    const focusKind = params.get("kind") || "durable";
    if (!focusId && !focusTour) return;
    // Always strip the query so a refresh doesn't reopen the dialog and a
    // stale link doesn't persist in the address bar.
    window.history.replaceState({}, "", window.location.pathname);
    if (focusId) {
      const asg = assignments.find((a) => a.id === focusId);
      if (asg) {
        const t = tours.find((x) => x.tour_id === asg.tour_id);
        if (t) setDialog({ tour: t, kind: asg.kind, mode: "edit", assignment: asg });
      }
    } else if (focusTour) {
      const t = tours.find((x) => x.tour_id === focusTour);
      if (t) {
        const asg = assignments.find((a) => a.tour_id === focusTour && a.kind === focusKind);
        setDialog({ tour: t, kind: focusKind, mode: asg ? "edit" : "create", assignment: asg });
      } else {
        toast.warning("Tournée introuvable — sélectionnez-la dans la liste");
      }
    }
  }, [loading, assignments, tours]);

  // Realtime: any tour_assignment mutation refetches the list (single source).
  useRealtime(["tour_assignments"], {
    "assignment.created":        () => load(),
    "assignment.updated":        () => load(),
    "assignment.deleted":        () => load(),
    "assignment.moved":          () => load(),
    "assignment.driver_changed": () => load(),
    "assignment.upserted":       () => load(),
  });

  const filtered = useMemo(() => {
    return assignments.filter((a) => {
      const matchKind = filterKind === "all" || a.kind === filterKind;
      const matchTour = !filterTour || a.tour_id === filterTour;
      return matchKind && matchTour;
    });
  }, [assignments, filterKind, filterTour]);

  const onRemove = async (id) => {
    if (!window.confirm("Supprimer cette affectation ?")) return;
    try {
      await api.delete(`/tour-assignments/${id}`);
      toast.success("Affectation supprimée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] bg-zinc-100" data-testid="fleet-assignments-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white border-b-2 border-[#FFC60A] flex items-center px-3 py-2 gap-3 flex-shrink-0">
        <div className="flex items-center gap-2 mr-2">
          <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#FFC60A]">FLOTTE · Exploitation</div>
          <div className="font-display font-black text-lg tracking-tighter">AFFECTATIONS</div>
        </div>
        <Kpi label="Tournées" value={tours.length} testId="fa-kpi-tours" />
        <Kpi label="Affectées" value={tours.filter((t) => t.assignment?.vehicle_id).length} accent="#10B981" testId="fa-kpi-assigned" />
        <Kpi label="Chauffeur manquant" value={tours.filter((t) => t.assignment?.vehicle_id && !t.assignment?.driver_id).length} accent={tours.filter((t) => t.assignment?.vehicle_id && !t.assignment?.driver_id).length ? "#F59E0B" : null} testId="fa-kpi-nodriver" />
        <Kpi label="À affecter" value={tours.filter((t) => !t.assignment?.vehicle_id).length} accent={tours.filter((t) => !t.assignment?.vehicle_id).length ? "#F59E0B" : null} testId="fa-kpi-unassigned" />
        <Kpi label="Conflits" value={conflicts.length} accent={conflicts.length ? "#EF4444" : null} testId="fa-kpi-conflicts" />
        <Kpi label="Exceptions actives" value={assignments.filter((a) => a.kind === "override" && (!a.start_date || a.start_date <= todayIso) && (!a.end_date || a.end_date >= todayIso)).length} testId="fa-kpi-overrides" />
        <div className="flex-1" />
        <button onClick={() => setPdfOpen(!pdfOpen)} data-testid="fa-pdf-btn"
          className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-1">
          <FileDown size={11} /> Exporter PDF
        </button>
        <button onClick={load} disabled={loading} data-testid="fa-refresh"
          className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
        <GenerateMissionsButton onDone={load} />
      </header>

      {pdfOpen && (
        <div className="bg-white border-b border-zinc-200 px-4 py-3 flex items-end gap-3 flex-wrap flex-shrink-0" data-testid="fa-pdf-panel">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Planning</div>
            <select value={pdfScope} onChange={(e) => { setPdfScope(e.target.value); setPdfTarget(""); }}
              className="border border-zinc-300 px-2 py-1.5 text-xs" data-testid="fa-pdf-scope">
              <option value="bus">Par bus</option>
              <option value="driver">Par chauffeur</option>
              <option value="day">Tous les bus</option>
            </select>
          </div>
          {pdfScope !== "day" && (
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">{pdfScope === "bus" ? "Bus" : "Chauffeur"}</div>
              <select value={pdfTarget} onChange={(e) => setPdfTarget(e.target.value)}
                className="border border-zinc-300 px-2 py-1.5 text-xs max-w-[220px]" data-testid="fa-pdf-target">
                <option value="">— Choisir —</option>
                {(pdfScope === "bus" ? vehicles : drivers).map((x) => (
                  <option key={x.id} value={x.id}>{pdfScope === "bus" ? (x.model || x.label || x.plate || x.id) : (x.name || x.id)}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Période</div>
            <select value={pdfPeriod} onChange={(e) => setPdfPeriod(e.target.value)}
              className="border border-zinc-300 px-2 py-1.5 text-xs" data-testid="fa-pdf-period">
              <option value="day">Journalier (aujourd&apos;hui)</option>
              <option value="week">Hebdomadaire</option>
            </select>
          </div>
          {pdfPeriod === "week" && (
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Format</div>
              <select value={pdfLayout} onChange={(e) => setPdfLayout(e.target.value)}
                className="border border-zinc-300 px-2 py-1.5 text-xs" data-testid="fa-pdf-layout">
                <option value="condensed">Semaine condensée (dépôt)</option>
                <option value="per_day">1 page par jour (chauffeur)</option>
              </select>
            </div>
          )}
          <button
            onClick={async () => {
              if (pdfScope !== "day" && !pdfTarget) { toast.error("Choisissez un bus ou un chauffeur"); return; }
              try {
                localStorage.setItem("logibus_pdf_layout", pdfLayout);
                const date_iso = new Date().toISOString().slice(0, 10);
                const params = { scope: pdfScope, date_iso, period: pdfPeriod, layout: pdfLayout };
                if (pdfScope !== "day") params.target_id = pdfTarget;
                const { data } = await api.get("/planning/pdf", { params, responseType: "blob" });
                const url = URL.createObjectURL(data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `logibus_planning_${pdfScope}_${date_iso}_${pdfPeriod}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("PDF généré");
              } catch { toast.error("Export PDF impossible"); }
            }}
            className="px-3 py-1.5 bg-zinc-900 text-[#FFC60A] text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-zinc-800"
            data-testid="fa-pdf-download"
          >
            <FileDown size={12} /> Télécharger
          </button>
        </div>
      )}

      {/* Body */}
      {/* Logitrak sync card — visible synchronization controls + counters
          + clear ownership documentation. Auto-triggers a real sync on
          mount (debounced if a successful sync already happened within
          the last 2 minutes). */}
      <LogitrakSyncCard data-testid="fa-navixy-card" />

      {/* Source-of-truth banner — clarifies that this page is the only
          place to modify fleet assignments. Planning Bus and Exploitation
          Services merely display the result. */}
      <div data-testid="fa-source-banner"
            className="bg-[#FFC60A]/15 border-y border-[#FFC60A] px-4 py-2 flex items-center gap-3 text-sm flex-shrink-0">
        <ShieldCheck size={16} className="text-zinc-900 shrink-0" />
        <div className="flex-1">
          <strong className="font-display font-black tracking-tight">Affectations flotte</strong>
          <span className="text-zinc-700">
            {" "}— vue globale des <strong>affectations principales</strong> et des <strong>exceptions ponctuelles</strong>.
            {" "}Les modifications effectuées ici ou depuis la <strong>fiche véhicule</strong> utilisent la même source de données
            {" "}(registre d&apos;affectations). Planning Bus, Missions et Exploitation lisent l&apos;affectation effective qui en résulte.
          </span>
        </div>
      </div>

      {/* Affectation groupée — visible dès qu'il existe des tournées sans
          affectation. Réutilise POST /tour-assignments (aucune logique
          parallèle) ; le backend resynchronise les missions déjà générées. */}
      <BulkAssignPanel tours={tours} vehicles={vehicles} drivers={drivers} onDone={load} />

      {/* View switcher: Synthèse (par défaut, v10.1) · Weekly · Bus · Tournées */}
      <div className="bg-white border-b border-zinc-200 flex items-center gap-px" data-testid="fa-tabs">
        {[
          { id: "synthesis", label: "Synthèse (Bus)",          testId: "fa-tab-synthesis" },
          { id: "weekly",    label: "Planning hebdomadaire",   testId: "fa-tab-weekly" },
          { id: "buses",     label: "Vue bus",                 testId: "fa-tab-buses" },
          { id: "tours",     label: "Vue tournées",            testId: "fa-tab-tours" },
        ].map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={t.testId}
            className={`px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors border-b-2 ${
              activeTab === t.id
                ? "border-[#FFC60A] text-zinc-900 bg-[#FFC60A]/10"
                : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === "synthesis" ? (
          <SynthesisView vehicles={vehicles} drivers={drivers} tours={tours} assignments={assignments} loading={loading} />
        ) : activeTab === "weekly" ? (
          <WeeklyPlanningView
            tours={tours}
            vehicles={vehicles}
            drivers={drivers}
            onEditCell={(tour, kind, assignment) =>
              setDialog({ tour, kind, mode: assignment ? "edit" : "create", assignment })}
          />
        ) : activeTab === "buses" ? (
          <BusesView
            tours={tours}
            onEditCell={(tour, kind, assignment) =>
              setDialog({ tour, kind, mode: assignment ? "edit" : "create", assignment })}
          />
        ) : (
        <>
        {/* Filters */}
        <div className="bg-white border border-zinc-200 px-3 py-2 mb-4 flex items-center gap-3 text-xs">
          <Filter size={12} className="text-zinc-500" />
          <div className="flex items-center gap-1" data-testid="fa-filter-kind">
            {["all", "durable", "override"].map((k) => (
              <button key={k} onClick={() => setFilterKind(k)} data-testid={`fa-filter-kind-${k}`}
                className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest ${filterKind === k ? "border-zinc-900 bg-zinc-900 text-[#FFC60A]" : "border-zinc-300 hover:border-zinc-900"}`}>
                {k === "all" ? "Tous" : k === "durable" ? "Durables" : "Overrides"}
              </button>
            ))}
          </div>
          <select value={filterTour} onChange={(e) => setFilterTour(e.target.value)} data-testid="fa-filter-tour"
            className="border border-zinc-300 px-2 py-1 text-xs bg-white">
            <option value="">Toutes les tournées</option>
            {tours.map((t) => <option key={t.tour_id} value={t.tour_id}>{t.code} {t.day_name} · {t.client_name}</option>)}
          </select>
        </div>

        {/* Tours grid (one tile per tour with its current durable assignment) */}
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-2 font-bold">Tournées importées</div>
          {loading && tours.length === 0 ? (
            <div className="text-center py-8 text-zinc-500"><Loader2 size={18} className="inline animate-spin" /> Chargement…</div>
          ) : tours.length === 0 ? (
            <div className="p-6 text-center text-zinc-400 bg-white border border-zinc-200">
              Aucune tournée importée. Allez dans <strong>Tournées scolaires</strong> pour importer un fichier Excel.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="fa-tours-grid">
              {tours.map((t) => (
                <TourCard key={t.tour_id} tour={t}
                  onAssign={(kind) => setDialog({ tour: t, kind, mode: "create" })}
                  onEdit={(asgFromList) => setDialog({ tour: t, kind: asgFromList.kind, mode: "edit", assignment: asgFromList })} />
              ))}
            </div>
          )}
        </div>

        {/* Assignments table */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-2 font-bold flex items-center justify-between">
            <span>Affectations actives ({filtered.length})</span>
          </div>
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="fa-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Tournée</th>
                  <th className="text-left px-3 py-2">Véhicule</th>
                  <th className="text-left px-3 py-2">Chauffeur</th>
                  <th className="text-left px-3 py-2">Début</th>
                  <th className="text-left px-3 py-2">Fin</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-6 text-zinc-400 text-xs">Aucune affectation ne correspond aux filtres.</td></tr>
                ) : filtered.map((a) => (
                  <tr key={a.id} data-testid={`fa-row-${a.id}`} className="hover:bg-zinc-50">
                    <td className="px-3 py-2"><KindBadge kind={a.kind} /></td>
                    <td className="px-3 py-2 font-bold">{a.tour_label || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs" title={a.vehicle_id || ""}>{a.vehicle_label || (a.vehicle_id ? "Bus inconnu" : "—")}</td>
                    <td className="px-3 py-2 text-xs">{a.driver_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{a.start_date || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{a.end_date || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-flex items-center gap-1 ${a.status === "active" ? "text-emerald-700" : "text-zinc-500"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${a.status === "active" ? "bg-emerald-500" : "bg-zinc-400"}`} />
                        {a.status === "active" ? "Actif" : a.status === "ended" ? "Terminé" : a.status === "pending" ? "En attente" : a.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => onRemove(a.id)} data-testid={`fa-row-delete-${a.id}`}
                        className="text-zinc-500 hover:text-red-600 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-1 border border-transparent hover:border-red-200">
                        <Trash2 size={11} /> Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>

      {dialog && (
        <AssignmentDialog dialog={dialog} vehicles={vehicles} drivers={drivers}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}

/* ── Affectation groupée (bulk) ─────────────────────────────────────────
   Une ligne par tournée SANS affectation : bus + chauffeur, puis
   "Affecter tout" enchaîne les POST /tour-assignments existants. */
function BulkAssignPanel({ tours, vehicles, drivers, onDone }) {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState({}); // tour_id -> {vehicle_id, driver_id}
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);

  const unassigned = useMemo(() => tours.filter((t) => !t.assignment), [tours]);
  if (unassigned.length === 0) return null;

  const setRow = (tourId, patch) =>
    setRows((r) => ({ ...r, [tourId]: { ...(r[tourId] || {}), ...patch } }));

  const applyVehicleEverywhere = async (vehicleId) => {
    const veh = vehicles.find((v) => v.id === vehicleId);
    try {
      // Contrôle backend AVANT tout préremplissage : jours + semaine A/B +
      // validité + heures. Aucune ligne conflictuelle ou déjà affectée n'est touchée.
      const { data } = await api.post("/tour-assignments/bulk-check", {
        vehicle_id: vehicleId,
        tour_ids: unassigned.map((t) => t.tour_id),
      });
      const verdicts = Object.fromEntries((data.results || []).map((r) => [r.tour_id, r]));
      setRows((r) => {
        const next = { ...r };
        unassigned.forEach((t) => {
          if (verdicts[t.tour_id]?.verdict === "ok") {
            next[t.tour_id] = { ...(next[t.tour_id] || {}), vehicle_id: vehicleId,
              driver_id: next[t.tour_id]?.driver_id || veh?.primary_driver_id || "" };
          }
        });
        return next;
      });
      const s = data.summary || {};
      toast[s.conflict ? "warning" : "success"](
        `${unassigned.length} tournées analysées — ${s.ok || 0} préremplie(s) · ` +
        `${s.conflict || 0} ignorée(s) (conflit horaire) · ${s.already_assigned || 0} ignorée(s) (déjà affectée)`,
        { duration: 6000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const readyRows = unassigned.filter((t) => rows[t.tour_id]?.vehicle_id);

  const assignAll = async () => {
    if (readyRows.length === 0) { toast.error("Sélectionnez au moins un bus"); return; }
    setRunning(true);
    let ok = 0, ko = 0, missionsUpdated = 0;
    for (let i = 0; i < readyRows.length; i++) {
      const t = readyRows[i];
      const sel = rows[t.tour_id];
      setProgress({ done: i, total: readyRows.length, current: t.code });
      try {
        const res = await api.post("/tour-assignments", {
          tour_id: t.tour_id,
          vehicle_id: sel.vehicle_id,
          driver_id: sel.driver_id || null,
          kind: "durable",
          start_date: todayIsoLocal(),
          end_date: null,
          notes: "Affectation groupée",
          force: true,
        });
        ok += 1;
        missionsUpdated += res.data?.missions_resync?.updated || 0;
      } catch (e) { ko += 1; }
    }
    setProgress(null);
    setRunning(false);
    setRows({});
    if (ko === 0) toast.success(`${ok} tournée${ok > 1 ? "s" : ""} affectée${ok > 1 ? "s" : ""} — ${missionsUpdated} missions mises à jour dans le planning`);
    else toast.warning(`${ok} affectées, ${ko} en échec — ${missionsUpdated} missions mises à jour`);
    onDone();
  };

  return (
    <div className="bg-amber-50 border-y border-amber-300 flex-shrink-0" data-testid="bulk-assign-panel">
      <button onClick={() => setOpen((v) => !v)} data-testid="bulk-assign-toggle"
        className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-amber-100/60">
        <AlertTriangle size={15} className="text-amber-700 shrink-0" />
        <div className="flex-1 text-left">
          <strong className="font-display font-black tracking-tight">{unassigned.length} tournée{unassigned.length > 1 ? "s" : ""} sans bus</strong>
          <span className="text-zinc-700"> — leurs missions restent « À affecter » et n&apos;apparaissent pas sur les lignes du Planning Bus. Affectez-les ici en une fois.</span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-amber-800">{open ? "Réduire ▲" : "Ouvrir ▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 mb-2 text-[11px]">
            <span className="font-mono uppercase tracking-widest text-zinc-500">Remplissage rapide :</span>
            <select defaultValue="" data-testid="bulk-assign-fill-vehicle"
              onChange={(e) => { if (e.target.value) applyVehicleEverywhere(e.target.value); e.target.value = ""; }}
              className="border border-zinc-300 px-2 py-1 bg-white text-xs">
              <option value="">Appliquer un bus à toutes les lignes…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{[v.model, v.plate].filter(Boolean).join(" · ") || v.label || v.id.slice(0, 8)}</option>)}
            </select>
          </div>
          <div className="bg-white border border-zinc-200 max-h-72 overflow-y-auto">
            <table className="w-full text-xs" data-testid="bulk-assign-table">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                <tr className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-2 py-1.5">Tournée</th>
                  <th className="text-left px-2 py-1.5">Jour</th>
                  <th className="text-left px-2 py-1.5">Bus</th>
                  <th className="text-left px-2 py-1.5">Chauffeur (optionnel)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {unassigned.map((t) => {
                  const sel = rows[t.tour_id] || {};
                  return (
                    <tr key={t.tour_id} data-testid={`bulk-assign-row-${t.tour_id}`} className={sel.vehicle_id ? "bg-emerald-50/50" : ""}>
                      <td className="px-2 py-1 font-bold whitespace-nowrap">{t.code} <span className="font-normal text-zinc-500">{t.client_name}</span></td>
                      <td className="px-2 py-1 text-zinc-600 whitespace-nowrap">{t.day_name || "—"}</td>
                      <td className="px-2 py-1">
                        <select value={sel.vehicle_id || ""} data-testid={`bulk-assign-vehicle-${t.tour_id}`}
                          onChange={(e) => {
                            const veh = vehicles.find((v) => v.id === e.target.value);
                            setRow(t.tour_id, { vehicle_id: e.target.value,
                              driver_id: sel.driver_id || veh?.primary_driver_id || "" });
                          }}
                          className="w-full border border-zinc-300 px-1.5 py-1 bg-white">
                          <option value="">— choisir —</option>
                          {vehicles.map((v) => <option key={v.id} value={v.id}>{[v.model, v.plate].filter(Boolean).join(" · ") || v.label || v.id.slice(0, 8)}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <select value={sel.driver_id || ""} data-testid={`bulk-assign-driver-${t.tour_id}`}
                          onChange={(e) => setRow(t.tour_id, { driver_id: e.target.value })}
                          className="w-full border border-zinc-300 px-1.5 py-1 bg-white">
                          <option value="">— aucun —</option>
                          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-2">
            {progress && (
              <span className="text-[11px] font-mono text-zinc-600">
                {progress.done}/{progress.total} — {progress.current}…
              </span>
            )}
            <div className="flex-1" />
            <button onClick={assignAll} disabled={running || readyRows.length === 0}
              data-testid="bulk-assign-submit"
              className="px-4 py-2 bg-zinc-900 text-[#FFC60A] text-[11px] font-black uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {running ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
              Affecter {readyRows.length > 0 ? `${readyRows.length} tournée${readyRows.length > 1 ? "s" : ""}` : "les tournées"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div className="border border-zinc-700 bg-zinc-800/50 px-3 py-1.5 min-w-[100px]" data-testid={testId}>
      <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-zinc-400">{label}</div>
      <div className="font-display font-black text-xl tracking-tighter leading-none mt-0.5" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

function KindBadge({ kind }) {
  const meta = kind === "override"
    ? { label: "Override", color: "#A855F7", bg: "#FAE8FF" }
    : { label: "Durable", color: "#0EA5E9", bg: "#E0F2FE" };
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-widest border"
      style={{ color: meta.color, borderColor: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
}

/* ── Tour card ─────────────────────────────────────────────────────────── */
function TourCard({ tour, onAssign, onEdit }) {
  const asg = tour.assignment;
  const accent = asg ? "#0EA5E9" : "#F59E0B";
  return (
    <div className="bg-white border-l-4 border border-zinc-200 shadow-[2px_2px_0_0_#0A0A0B] p-3" style={{ borderLeftColor: accent }}
      data-testid={`fa-tour-${tour.tour_id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-display font-black text-lg tracking-tighter px-1.5 py-0.5 bg-zinc-900 text-[#FFC60A] inline-block">{tour.code}</span>
            <span className="text-xs font-bold text-zinc-700">{tour.day_name}</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{tour.client_name}</div>
        </div>
      </div>
      <div className="text-[10px] font-mono text-zinc-500 mb-2 truncate">
        {tour.first_time && <span>{tour.first_time} → {tour.last_time} · </span>}
        {tour.stops_count} arrêts
      </div>
      {asg ? (
        <div className="bg-sky-50 border border-sky-200 px-2 py-1.5 mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-sky-800 font-bold flex items-center gap-1 mb-0.5">
            <CheckCircle2 size={10} /> Affecté
          </div>
          <div className="text-xs"><strong>{asg.vehicle_label}</strong>{asg.driver_name && <span className="text-zinc-600"> · {asg.driver_name}</span>}</div>
          <div className="text-[10px] font-mono text-zinc-500">{asg.start_date} → {asg.end_date}</div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 px-2 py-1.5 mb-2 text-[11px] text-amber-900 font-medium flex items-center gap-1.5">
          <AlertCircle size={11} /> Aucun véhicule affecté
        </div>
      )}
      <div className="flex items-center gap-1">
        {!asg && (
          <button onClick={() => onAssign("durable")} data-testid={`fa-tour-assign-${tour.tour_id}`}
            className="flex-1 px-2 py-1 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center justify-center gap-1">
            <Plus size={10} /> Affecter durable
          </button>
        )}
        <button onClick={() => onAssign("override")} data-testid={`fa-tour-override-${tour.tour_id}`}
          className="flex-1 px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 text-[10px] font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1">
          <Plus size={10} /> Remplacement ponctuel
        </button>
      </div>
    </div>
  );
}

/* ── Dialog ────────────────────────────────────────────────────────────── */
function AssignmentDialog({ dialog, vehicles, drivers, onClose, onDone }) {
  const { tour, kind, mode, assignment } = dialog;
  const isOverride = kind === "override";
  // The active durable for this tour (used to display "Affectation actuelle")
  // GET /api/tours surfaces only the current durable in `tour.assignment` (no `kind` field).
  const currentDurable = tour?.assignment || null;
  const [vehicleId, setVehicleId] = useState(assignment?.vehicle_id || "");
  const [driverId, setDriverId] = useState(assignment?.driver_id || "");
  const [startDate, setStartDate] = useState(assignment?.start_date || (kind === "durable" ? "2025-08-25" : ""));
  const [endDate, setEndDate] = useState(assignment?.end_date || (kind === "durable" ? "2026-07-03" : ""));
  const [notes, setNotes] = useState(assignment?.notes || "");
  const [saving, setSaving] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);

  // Pre-validate conflicts whenever the inputs change. Only the durable
  // path triggers an overlap check; overrides are by definition local.
  useEffect(() => {
    if (kind !== "durable" || !vehicleId) {
      setConflictWarning(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/tour-assignments/validate-conflicts", {
          tour_id: tour.tour_id,
          vehicle_id: vehicleId,
          driver_id: driverId || null,
          start_date: startDate || null,
          end_date: endDate || null,
          exclude_assignment_id: assignment?.id || null,
        });
        setConflictWarning(data.has_conflicts ? data : null);
      } catch (e) { /* silent — not blocking save */ }
    }, 350);
    return () => clearTimeout(handle);
  }, [kind, vehicleId, driverId, startDate, endDate, tour.tour_id, assignment?.id]);

  const save = async () => {
    if (isOverride) {
      if (!startDate || !endDate) {
        toast.error("Exception ponctuelle : début et fin sont obligatoires"); return;
      }
      // Override must change at least one of (vehicle, driver) vs the durable
      const sameVeh = !vehicleId || (currentDurable && vehicleId === currentDurable.vehicle_id);
      const sameDrv = !driverId  || (currentDurable && driverId  === currentDurable.driver_id);
      if (sameVeh && sameDrv) {
        toast.error("Choisissez un nouveau véhicule ou un nouveau chauffeur pour créer l'exception");
        return;
      }
    } else if (!vehicleId) {
      toast.error("Sélectionnez un véhicule"); return;
    }
    // Forced override confirmation when conflicts detected.
    if (conflictWarning?.has_conflicts) {
      const ok = window.confirm(
        `⚠️ Conflit détecté : ${conflictWarning.summary}.\n\n` +
        "Voulez-vous outrepasser ce conflit ? L'action sera enregistrée " +
        "dans le journal d'audit comme une affectation forcée.",
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = {
        tour_id: tour.tour_id,
        vehicle_id: vehicleId || (isOverride ? currentDurable?.vehicle_id : null),
        driver_id:  driverId  || (isOverride ? currentDurable?.driver_id  : null),
        start_date: startDate || null, end_date: endDate || null, kind, notes,
        // Forced override → mark explicitly so the backend logs it.
        force: !!conflictWarning?.has_conflicts,
      };
      if (mode === "edit" && assignment?.id) {
        await api.patch(`/tour-assignments/${assignment.id}`, payload);
      } else {
        await api.post("/tour-assignments", payload);
      }
      toast.success(mode === "edit" ? "Affectation modifiée"
                    : isOverride ? "Exception ponctuelle créée"
                    : "Affectation durable créée");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" data-testid="fa-dialog">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-2xl flex flex-col">
        <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3">
          <LinkIcon size={16} className="text-[#FFC60A]" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">
              {isOverride ? "Exception ponctuelle" : "Affectation durable"}
            </div>
            <div className="font-display font-black text-lg tracking-tighter">
              {tour.code} · {tour.day_name} · {tour.client_name}
            </div>
          </div>
          <button onClick={onClose} data-testid="fa-dialog-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </header>

        {isOverride && currentDurable && (
          <div className="px-4 pt-4" data-testid="fa-dialog-current-assignment">
            <div className="border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-700 font-bold mb-1">
                Affectation actuelle (durable, conservée)
              </div>
              <div className="text-sm space-y-0.5">
                <div><span className="text-zinc-500">Véhicule :</span> <strong>{currentDurable.vehicle_label || "—"}</strong></div>
                <div><span className="text-zinc-500">Chauffeur :</span> <strong>{currentDurable.driver_name || "— non défini —"}</strong></div>
                <div><span className="text-zinc-500">Période :</span> <span className="font-mono text-xs">{currentDurable.start_date || "—"} → {currentDurable.end_date || "—"}</span></div>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-zinc-600 bg-zinc-50 border-l-2 border-zinc-900 px-2 py-1.5">
              L&apos;exception ponctuelle s&apos;applique <strong>uniquement sur la période choisie</strong>. L&apos;affectation durable reprend ensuite automatiquement.
            </div>
          </div>
        )}
        {isOverride && !currentDurable && (
          <div className="px-4 pt-4" data-testid="fa-dialog-no-durable">
            <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-800 font-bold mb-1 flex items-center gap-1">
                <AlertCircle size={11} /> Aucune affectation durable
              </div>
              Cette tournée n&apos;a pas encore d&apos;affectation durable. Vous pouvez créer une exception ponctuelle pour une période courte, mais il est recommandé de définir d&apos;abord une <strong>affectation durable</strong> pour l&apos;année scolaire.
            </div>
          </div>
        )}

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={isOverride ? "Nouveau véhicule" : "Véhicule"}>
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="fa-dialog-vehicle"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white">
              <option value="">{isOverride
                ? (currentDurable ? "— Garder le véhicule actuel —" : "— Choisir un véhicule —")
                : "— Choisir un véhicule —"}</option>
              {vehicles.map((v) => {
                const name = v.model || v.label || v.plate || v.license_plate || (v.id || "").slice(0, 8);
                const cap = v.capacity ? ` · ${v.capacity}+${v.pmr_seats || 0}` : "";
                return <option key={v.id} value={v.id}>{name}{cap}</option>;
              })}
            </select>
          </Field>
          <Field label={isOverride ? "Nouveau chauffeur" : "Chauffeur (facultatif)"}>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)} data-testid="fa-dialog-driver"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white">
              <option value="">{isOverride
                ? (currentDurable ? "— Garder le chauffeur actuel —" : "— Aucun —")
                : "— Aucun —"}</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
            </select>
          </Field>
          <Field label="Date de début">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="fa-dialog-start"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Date de fin">
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="fa-dialog-end"
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm" />
          </Field>
          <Field label={isOverride ? "Motif" : "Notes"} full>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="fa-dialog-notes"
              placeholder={isOverride ? "Ex: Bus 48 en maintenance, chauffeur en formation, sortie scolaire…" : "Affectation annuelle"}
              className="w-full border border-zinc-300 px-2 py-1.5 text-sm" />
          </Field>
        </div>

        {/* Historique des modifications */}
        <AssignmentHistory tourId={tour.tour_id} assignmentId={assignment?.id} />

        {/* Conflict warning — pre-validates against bus/driver overlaps. */}
        {conflictWarning?.has_conflicts && (
          <div className="px-4 pb-3" data-testid="fa-dialog-conflict-warning">
            <div className="border-2 border-amber-500 bg-amber-50 px-3 py-2 shadow-[3px_3px_0_0_#F59E0B]">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-amber-700 shrink-0" />
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-900 font-bold">
                  Conflit détecté — {conflictWarning.summary}
                </div>
              </div>
              <ul className="text-[11px] text-zinc-700 space-y-0.5">
                {(conflictWarning.conflicts?.vehicle || []).map((c) => (
                  <li key={`v-${c.id}`} className="font-mono">
                    🚌 Bus → tournée <strong title={c.tour_id}>{c.tour_label || "Tournée supprimée"}</strong> · {c.start_date} → {c.end_date || "∞"}
                  </li>
                ))}
                {(conflictWarning.conflicts?.driver || []).map((c) => (
                  <li key={`d-${c.id}`} className="font-mono">
                    👤 Chauffeur → tournée <strong title={c.tour_id}>{c.tool_label || c.tour_label || "Tournée supprimée"}</strong> · {c.start_date} → {c.end_date || "∞"}
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[10px] text-zinc-600 italic">
                Vous pouvez sauvegarder quand même — l&apos;action sera tracée comme un forçage dans le journal d&apos;audit.
              </div>
            </div>
          </div>
        )}

        <footer className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2">
          <button onClick={onClose} disabled={saving} data-testid="fa-dialog-cancel"
            className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest">
            Annuler
          </button>
          <div className="flex-1" />
          <button onClick={save} disabled={saving || (!isOverride && !vehicleId) || (isOverride && !vehicleId && !driverId)} data-testid="fa-dialog-save"
            className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
            {mode === "edit" ? "Enregistrer"
              : isOverride ? "Créer l'exception ponctuelle"
              : "Créer l'affectation"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ── AssignmentHistory ───────────────────────────────────────────────────
 * Collapsible "Historique" panel inside AssignmentDialog. Pulls
 * `/api/assignment-events?tour_id=` lazily on expand.
 */
function AssignmentHistory({ tourId, assignmentId }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (!tourId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/assignment-events?tour_id=${tourId}&limit=50`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (open && events === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="px-4 pb-3" data-testid="fa-dialog-history">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="fa-dialog-history-toggle"
        className="w-full px-3 py-2 border border-zinc-200 bg-white hover:bg-zinc-50 text-left inline-flex items-center gap-2">
        <History size={13} className="text-zinc-500" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-700 font-bold">
          Historique des modifications
        </span>
        <span className="ml-auto text-[10px] font-mono text-zinc-400">
          {open ? "− Masquer" : "+ Afficher"}
        </span>
      </button>
      {open && (
        <div className="mt-2 border border-zinc-200 bg-zinc-50 max-h-64 overflow-y-auto"
              data-testid="fa-dialog-history-list">
          {loading && <div className="px-3 py-4 text-center text-zinc-400 text-sm"><Loader2 size={14} className="inline animate-spin mr-1" /> Chargement…</div>}
          {!loading && events && events.length === 0 && (
            <div className="px-3 py-4 text-center text-zinc-400 text-xs">Aucun événement enregistré.</div>
          )}
          {!loading && events && events.length > 0 && (
            <ul className="divide-y divide-zinc-200">
              {events.map((ev) => (
                <li key={ev.id} className={`px-3 py-2 text-xs ${ev.assignment_id === assignmentId ? "bg-yellow-50" : ""}`}
                    data-testid={`fa-history-event-${ev.id}`}>
                  <div className="flex items-center gap-2">
                    <ActionBadge action={ev.action} source={ev.source} />
                    <span className="font-mono text-[10px] text-zinc-500 tabular-nums">
                      {new Date(ev.created_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                    <span className="text-zinc-400">·</span>
                    <span className="text-zinc-700 font-medium truncate">
                      {ev.actor_name || (ev.source === "excel-import" ? "Import Excel" : "Système")}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-zinc-600 space-y-0.5">
                    <HistoryRow label="Bus" prev={ev.previous_vehicle?.plate} next={ev.vehicle?.plate} icon={Bus} />
                    <HistoryRow label="Chauffeur" prev={ev.previous_driver?.name} next={ev.driver?.name} icon={UserIcon} />
                    {(ev.start_date || ev.end_date) && (
                      <div className="flex items-center gap-1 text-zinc-500">
                        <Calendar size={10} />
                        <span className="font-mono">{ev.start_date || "?"} → {ev.end_date || "∞"}</span>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBadge({ action, source }) {
  const meta = {
    created:           { label: "Créée",        cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    auto_created:      { label: "Auto",         cls: "bg-sky-100 text-sky-800 border-sky-300" },
    override_created:  { label: "Exception", cls: "bg-purple-100 text-purple-800 border-purple-300" },
    updated:           { label: "Modifiée",     cls: "bg-amber-100 text-amber-900 border-amber-300" },
    deleted:           { label: "Supprimée",    cls: "bg-red-100 text-red-800 border-red-300" },
  }[action] || { label: action, cls: "bg-zinc-100 text-zinc-700 border-zinc-300" };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-mono font-bold uppercase tracking-widest ${meta.cls}`}>
      {action === "auto_created" || source === "excel-import" ? <Zap size={9} /> : null}
      {meta.label}
    </span>
  );
}

function HistoryRow({ label, prev, next, icon: Icon }) {
  if (!prev && !next) return null;
  const changed = prev !== next;
  return (
    <div className="flex items-center gap-1">
      <Icon size={10} className="text-zinc-400" />
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}:</span>
      {changed && prev && <span className="line-through text-zinc-400">{prev}</span>}
      {changed && prev && next && <ArrowRight size={9} className="text-zinc-400" />}
      <span className={changed ? "font-bold text-zinc-900" : "text-zinc-700"}>{next || prev || "—"}</span>
    </div>
  );
}


/* ── Logitrak sync card (top of /admin/fleet-assignments) ─────────────────
   Auto-triggers a real sync on mount (debounced) so the operator always
   sees fresh driver/vehicle data when opening the screen. Also exposes a
   manual "Synchroniser maintenant" button and a clear ownership note. */
const NAVIXY_AUTO_SYNC_MIN_INTERVAL_S = 120; // throttle the auto-sync
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}
function LogitrakSyncCard() {
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchSummary = React.useCallback(async () => {
    try {
      const r = await api.get("/navixy-sync/summary");
      setSummary(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, []);

  const runSync = React.useCallback(async ({ silent = false } = {}) => {
    if (running) return;
    setRunning(true);
    try {
      // Two scope-restricted calls so the progress feels granular.
      const r1 = await api.post("/navixy-sync/run", { scope: "drivers", dry_run: false });
      const r2 = await api.post("/navixy-sync/run", { scope: "affectations", dry_run: false });
      const w1 = r1.data?.written || {};
      const w2 = r2.data?.written || {};
      if (!silent) {
        const total = (w1.drivers_created || 0) + (w1.drivers_updated || 0)
          + (w2.affectations_updated || 0) + (w2.tracker_remap_applied || 0);
        toast.success(
          total > 0
            ? `Logitrak synchronisé — ${w1.drivers_created || 0} chauffeurs créés, ${w1.drivers_updated || 0} mis à jour, ${w2.affectations_updated || 0} liaisons véhicule-chauffeur, ${w2.tracker_remap_applied || 0} trackers remappés`
            : "Logitrak synchronisé — aucun changement",
          { duration: 5000 },
        );
      }
      await fetchSummary();
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Échec de la synchronisation Logitrak");
    } finally {
      setRunning(false);
    }
  }, [running, fetchSummary]);

  // On mount: fetch summary, then auto-sync if more than 2 min since last sync.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await fetchSummary();
      if (cancelled || !s || !s.configured) return;
      const ageS = s.last_sync_at
        ? (Date.now() - new Date(s.last_sync_at).getTime()) / 1000
        : Infinity;
      if (ageS > NAVIXY_AUTO_SYNC_MIN_INTERVAL_S) {
        runSync({ silent: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!summary) {
    return (
      <div className="bg-white border-b border-zinc-200 px-3 py-2 text-[11px] text-zinc-500" data-testid="fa-navixy-card">
        Chargement de la synchronisation Logitrak…
      </div>
    );
  }
  if (!summary.configured) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-center gap-2" data-testid="fa-navixy-card">
        <AlertCircle size={12} />
        Aucune clé Logitrak configurée — la synchronisation chauffeurs/véhicules est désactivée.
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 text-white border-b-2 border-[#FFC60A]" data-testid="fa-navixy-card">
      <div className="px-3 py-2 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A] shrink-0">
          <Radio size={11} /> Logitrak
        </div>
        <LogitrakStat label="Chauffeurs" value={summary.drivers_synced} testId="fa-navixy-drivers" />
        <LogitrakStat label="Véhicules" value={summary.vehicles_synced} testId="fa-navixy-vehicles" />
        <LogitrakStat label="Liaisons" value={summary.affectations_synced} testId="fa-navixy-liaisons" />
        <div className="text-[10px] text-zinc-400 ml-2 hidden md:block" data-testid="fa-navixy-last">
          Dernière sync&nbsp;: <span className="text-white font-mono">{fmtDateTime(summary.last_sync_at)}</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => runSync()}
          disabled={running}
          data-testid="fa-navixy-sync-btn"
          className="px-2.5 py-1 bg-[#FFC60A] text-zinc-900 text-[10px] font-bold uppercase tracking-widest border border-[#FFC60A] hover:bg-yellow-400 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {running ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {running ? "Synchronisation…" : "Synchroniser maintenant"}
        </button>
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="fa-navixy-info-toggle"
          className="px-2 py-1 border border-zinc-700 text-[10px] uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-1"
        >
          <Info size={11} /> {expanded ? "Masquer" : "Source de vérité"}
        </button>
      </div>
      {/* Mobile-only last sync line */}
      <div className="px-3 pb-1.5 text-[10px] text-zinc-400 md:hidden">
        Dernière sync&nbsp;: <span className="text-white font-mono">{fmtDateTime(summary.last_sync_at)}</span>
      </div>
      {expanded && (
        <div className="px-3 pb-3 bg-zinc-800/80 border-t border-zinc-700" data-testid="fa-navixy-contract">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FFC60A] mb-2 mt-2 inline-flex items-center gap-1.5">
            <ShieldCheck size={11} /> Source de vérité
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-[11px] leading-relaxed">
            <div className="border-l-2 border-emerald-400 pl-2">
              <div className="font-bold text-emerald-300 mb-0.5">Géré par Logitrak</div>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Conducteurs (employés Logitrak → utilisateurs Logibus rôle chauffeur)</li>
                <li>Véhicules (trackers Logitrak → véhicules Logibus, mappés par plaque)</li>
                <li>Liaison statique chauffeur ↔ véhicule (qui conduit habituellement quel bus)</li>
                <li>Position GPS, vitesse, état moteur (temps réel)</li>
                <li>POI / Zones d&apos;intérêt</li>
              </ul>
            </div>
            <div className="border-l-2 border-[#FFC60A] pl-2">
              <div className="font-bold text-[#FFC60A] mb-0.5">Géré par Logibus</div>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Affectations de tournées</strong> (durables et exceptions ponctuelles)</li>
                <li>Planning Bus &amp; Centre d&apos;exploitation</li>
                <li>Tournées importées via Excel, arrêts, horaires</li>
                <li>Missions du jour, incidents, journal</li>
                <li>Paramètres tenant, calendrier scolaire</li>
              </ul>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-zinc-400">
            <strong className="text-zinc-300">Source de vérité métier&nbsp;:</strong> le registre d&apos;Affectations
            (tournée + bus + chauffeur + validité). La fiche Véhicule et Affectations flotte sont deux interfaces
            qui gèrent ces mêmes affectations. Les Exceptions remplacent temporairement une affectation sans la supprimer.
            Synchronisations automatiques&nbsp;: au démarrage de Logibus, toutes les 15 minutes,
            et à l&apos;ouverture de cette page. Conflit éventuel&nbsp;: une affectation durable
            saisie dans Logibus n&apos;est <strong>jamais</strong> écrasée par Logitrak.
          </div>
        </div>
      )}
    </div>
  );
}

function LogitrakStat({ label, value, testId }) {
  return (
    <div className="flex items-baseline gap-1.5" data-testid={testId}>
      <span className="text-base font-display font-black text-[#FFC60A]">{value ?? "—"}</span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-300">{label}</span>
    </div>
  );
}

/* ── GenerateMissionsButton ─────────────────────────────────────────────
   Materializes day-level missions from active tour assignments for the
   next 30 days. Idempotent: re-running never creates duplicates.
   ──────────────────────────────────────────────────────────────────── */
function GenerateMissionsButton({ onDone }) {
  const [running, setRunning] = useState(false);
  const [last, setLast] = useState(null);
  const run = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await api.post("/missions/generate", {});
      setLast(data);
      toast.success(
        data.created > 0
          ? `${data.created} mission(s) créée(s) · ${data.pending} en attente d'affectation`
          : "Tout est déjà à jour"
      );
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };
  return (
    <button onClick={run} disabled={running} data-testid="fa-generate-missions"
      title="Génère les missions des 30 prochains jours à partir des affectations actives. Idempotent."
      className="px-2 py-1 border border-[#FFC60A] bg-[#FFC60A] text-zinc-900 text-[10px] font-black uppercase tracking-widest hover:bg-white hover:text-zinc-900 inline-flex items-center gap-1 disabled:opacity-50">
      {running ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
      {running ? "Génération…" : "Générer missions (30j)"}
      {last && !running && (
        <span className="ml-1 font-mono normal-case tracking-normal text-[9px]">· +{last.created}</span>
      )}
    </button>
  );
}

/* ── Weekly planning view ──────────────────────────────────────────────
   Grid: 1 row per bus × N day columns (Mon→Fri by default).
   Each cell shows tour code + times + driver, colour-coded by state.
   Click on a cell → edit assignment (or create override/durable).
   Header includes week selector (prev/next/today) and filters.
   ────────────────────────────────────────────────────────────────────── */
function _isoMonday(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - dow);
  return x.toISOString().slice(0, 10);
}
function _addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function _fmtFr(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

const CELL_STATE_META = {
  affected:    { bg: "bg-emerald-50",  border: "border-emerald-300", text: "text-emerald-900", dot: "bg-emerald-500", label: "Affecté" },
  partial:     { bg: "bg-sky-50",      border: "border-sky-300",     text: "text-sky-900",     dot: "bg-sky-500",     label: "Partiel" },
  replacement: { bg: "bg-amber-50",    border: "border-amber-300",   text: "text-amber-900",   dot: "bg-amber-500",   label: "Exception" },
  conflict:    { bg: "bg-red-50",      border: "border-red-400",     text: "text-red-900",     dot: "bg-red-500",     label: "Conflit" },
  free:        { bg: "bg-white",       border: "border-zinc-200",    text: "text-zinc-400",    dot: "bg-zinc-300",    label: "Libre" },
};

function WeeklyPlanningView({ tours, vehicles, drivers, onEditCell }) {
  const [weekStart, setWeekStart] = useState(() => _isoMonday());
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | free | conflict | no-driver | replacement | unassigned-tours

  const fetchGrid = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/fleet-assignments/grid?week_start=${weekStart}&days=5`);
      setGrid(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  // Realtime: any tour_assignment mutation refetches the grid
  useRealtime(["tour_assignments"], {
    "assignment.created":        () => fetchGrid(),
    "assignment.updated":        () => fetchGrid(),
    "assignment.deleted":        () => fetchGrid(),
    "assignment.moved":          () => fetchGrid(),
    "assignment.driver_changed": () => fetchGrid(),
    "assignment.upserted":       () => fetchGrid(),
  });

  const tourById = useMemo(() => {
    const m = {};
    for (const t of tours) m[t.tour_id] = t;
    return m;
  }, [tours]);

  const assignmentById = useMemo(() => {
    // Build an assignment lookup from tours' embedded `assignment` field
    const m = {};
    for (const t of tours) if (t.assignment?.id) m[t.assignment.id] = t.assignment;
    return m;
  }, [tours]);

  const drvById = useMemo(() => {
    const m = {};
    for (const d of drivers) m[d.id] = d;
    return m;
  }, [drivers]);

  // Tours not assigned for the current week → list as "Tournées non affectées"
  const unassignedTours = useMemo(() => tours.filter((t) => !t.assignment), [tours]);

  // Apply filter to vehicle rows
  const filteredVehicles = useMemo(() => {
    const list = grid?.vehicles || [];
    switch (filter) {
      case "free":
        return list.filter((v) => v.status === "free");
      case "conflict":
        return list.filter((v) => v.status === "conflict");
      case "no-driver":
        return list.filter((v) => !v.principal_driver);
      case "replacement":
        return list.filter((v) => v.replacement_count_week > 0);
      case "unassigned-tours":
        return []; // we render the unassigned-tours panel instead of the grid
      default:
        return list;
    }
  }, [grid, filter]);

  const onCellClick = (vehRow, dayIso, cell) => {
    // If there's an existing assignment on that day → edit it (override or durable)
    if (cell?.tour_id) {
      const tour = tourById[cell.tour_id];
      if (!tour) {
        toast.error("Tournée introuvable dans la liste");
        return;
      }
      if (cell.state === "replacement" || cell.state === "conflict") {
        const asg = assignmentById[cell.assignment_id];
        onEditCell(tour, "override", asg || null);
      } else {
        // affected → edit durable, or create override on top
        const asg = assignmentById[cell.assignment_id] || tour.assignment;
        onEditCell(tour, "durable", asg || null);
      }
      return;
    }
    // Free cell on this bus: offer to create a NEW assignment.
    // Strategy: ask user to pick a tour from the unassigned list via dialog.
    // For now, redirect them to the "Tournées non affectées" panel.
    toast.info("Sélectionnez une tournée dans le panneau « Tournées non affectées » ci-dessous pour l'affecter à ce bus.");
  };

  const stats = grid?.stats || {};

  return (
    <div className="space-y-4" data-testid="fa-weekly-view">
      {/* Toolbar: week selector + filters + stats */}
      <div className="bg-white border border-zinc-200 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="fa-weekly-toolbar">
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekStart(_addDays(weekStart, -7))} data-testid="fa-week-prev"
            className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 text-xs font-bold">← Sem. préc.</button>
          <button onClick={() => setWeekStart(_isoMonday())} data-testid="fa-week-today"
            className="px-2 py-1 border border-zinc-900 bg-zinc-900 text-[#FFC60A] text-[10px] font-black uppercase tracking-widest">Aujourd&apos;hui</button>
          <button onClick={() => setWeekStart(_addDays(weekStart, 7))} data-testid="fa-week-next"
            className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 text-xs font-bold">Sem. suiv. →</button>
        </div>
        <div className="text-xs font-mono text-zinc-600" data-testid="fa-week-range">
          <Calendar size={12} className="inline mr-1" />
          Semaine du <strong>{_fmtFr(grid?.week_start || weekStart)}</strong> au <strong>{_fmtFr(grid?.week_end || _addDays(weekStart, 4))}</strong>
        </div>

        <div className="flex-1" />

        {/* Filters */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="fa-weekly-filters">
          <Filter size={12} className="text-zinc-500" />
          {[
            { id: "all",               label: "Tous",                count: stats.total_buses },
            { id: "free",              label: "Libres",              count: stats.free },
            { id: "conflict",          label: "Conflits",            count: stats.conflict, danger: true },
            { id: "no-driver",         label: "Sans chauffeur" },
            { id: "replacement",       label: "Exceptions",          count: stats.replacement },
            { id: "unassigned-tours",  label: "Tournées non affectées", count: unassignedTours.length, warn: true },
          ].map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`fa-weekly-filter-${f.id}`}
              className={`px-2 py-1 border text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${
                filter === f.id
                  ? "border-zinc-900 bg-zinc-900 text-[#FFC60A]"
                  : f.danger ? "border-red-300 text-red-700 hover:border-red-600 hover:bg-red-50"
                  : f.warn  ? "border-amber-300 text-amber-700 hover:border-amber-600 hover:bg-amber-50"
                  : "border-zinc-300 hover:border-zinc-900"
              }`}>
              {f.label}{typeof f.count === "number" && <span className="opacity-70">({f.count})</span>}
            </button>
          ))}
        </div>

        <button onClick={fetchGrid} disabled={loading} data-testid="fa-weekly-refresh"
          className="px-2 py-1 border border-zinc-300 hover:border-zinc-900 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </div>

      {/* Unassigned-tours panel (always visible if filter selected, else collapsed banner) */}
      {filter === "unassigned-tours" && (
        <div className="bg-amber-50 border border-amber-300 p-3" data-testid="fa-unassigned-panel">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-800 font-bold mb-2 flex items-center gap-1.5">
            <AlertCircle size={11} /> {unassignedTours.length} tournée(s) sans affectation durable
          </div>
          {unassignedTours.length === 0 ? (
            <div className="text-xs text-amber-900">Toutes les tournées importées sont affectées. 🎯</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {unassignedTours.map((t) => (
                <button key={t.tour_id} data-testid={`fa-unassigned-${t.tour_id}`}
                  onClick={() => onEditCell(t, "durable", null)}
                  className="text-left bg-white border border-amber-300 hover:border-zinc-900 hover:shadow-[2px_2px_0_0_#0A0A0B] p-2 transition-all">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="font-display font-black text-sm tracking-tighter px-1.5 py-0.5 bg-zinc-900 text-[#FFC60A] inline-block">{t.code}</span>
                    <span className="text-xs font-bold text-zinc-700">{t.day_name}</span>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.client_name}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                    {t.first_time && <span>{t.first_time} → {t.last_time} · </span>}
                    {t.stops_count} arrêts
                  </div>
                  <div className="mt-1 text-[10px] font-bold text-amber-700 uppercase tracking-widest inline-flex items-center gap-1">
                    <Plus size={9} /> Affecter
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* The grid itself (hidden when unassigned-tours filter is active) */}
      {filter !== "unassigned-tours" && (
        loading && !grid ? (
          <div className="text-center py-12 text-zinc-500"><Loader2 size={18} className="inline animate-spin" /> Chargement de la grille…</div>
        ) : !filteredVehicles.length ? (
          <div className="p-8 text-center text-zinc-400 bg-white border border-zinc-200">
            Aucun bus ne correspond au filtre <strong>{filter}</strong>.
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="fa-weekly-grid">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-zinc-50 border-b-2 border-zinc-300 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-widest text-zinc-500 sticky left-0 bg-zinc-50 z-20 min-w-[200px] border-r border-zinc-200">Bus / Chauffeur principal</th>
                  {(grid?.days || []).map((d) => (
                    <th key={d.date} className="text-left px-2 py-2 font-mono uppercase text-[10px] tracking-widest text-zinc-500 border-r border-zinc-100 min-w-[160px]">
                      {d.name}<br /><span className="text-zinc-400 normal-case font-normal">{_fmtFr(d.date)}</span>
                    </th>
                  ))}
                  <th className="text-center px-2 py-2 font-mono uppercase text-[10px] tracking-widest text-zinc-500 w-[70px]">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filteredVehicles.map((v) => {
                  const vMeta = CELL_STATE_META[v.status] || CELL_STATE_META.free;
                  return (
                    <tr key={v.id} data-testid={`fa-weekly-row-${v.id}`} className="hover:bg-zinc-50/50">
                      <td className="px-3 py-2 sticky left-0 bg-white hover:bg-zinc-50/50 z-10 border-r border-zinc-200">
                        <div className="flex items-center gap-2">
                          <Bus size={14} className="text-zinc-400" />
                          <div className="min-w-0">
                            <div className="font-bold text-zinc-900 font-mono text-xs truncate">{v.plate || v.label}</div>
                            <div className="text-[10px] text-zinc-500 truncate">
                              {v.principal_driver?.name || <span className="text-amber-700 font-bold">Aucun chauffeur</span>}
                              {v.capacity ? <span className="text-zinc-400"> · {v.capacity}+{v.pmr_seats || 0}pl</span> : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      {(grid?.days || []).map((d) => {
                        const cell = v.days?.[d.date] || { state: "free" };
                        const meta = CELL_STATE_META[cell.state] || CELL_STATE_META.free;
                        return (
                          <td key={d.date} className="border-r border-zinc-100 p-0 align-top">
                            <button
                              onClick={() => onCellClick(v, d.date, cell)}
                              data-testid={`fa-cell-${v.id}-${d.date}`}
                              className={`w-full h-full text-left px-2 py-1.5 border-l-4 ${meta.border} ${meta.bg} hover:brightness-95 transition-all min-h-[56px]`}>
                              {cell.tour_code && cell.tour_code !== "?" ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                    <span className={`font-display font-black text-sm tracking-tighter ${meta.text}`}>{cell.tour_code}</span>
                                  </div>
                                  {(cell.start || cell.end) && (
                                    <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
                                      {cell.start || "?"}{cell.end ? `→${cell.end}` : ""}
                                    </div>
                                  )}
                                  {cell.driver_name && (
                                    <div className="text-[10px] text-zinc-500 truncate">↳ {cell.driver_name}</div>
                                  )}
                                </>
                              ) : (
                                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-300">— libre —</div>
                              )}
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border ${vMeta.border} ${vMeta.bg} ${vMeta.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${vMeta.dot}`} />
                          {vMeta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Legend */}
      <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-[10px] font-mono text-zinc-600 flex items-center gap-4 flex-wrap" data-testid="fa-weekly-legend">
        <span className="uppercase tracking-widest font-bold text-zinc-500">Légende :</span>
        {Object.entries(CELL_STATE_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className={`w-2.5 h-2.5 inline-block ${m.bg} border ${m.border}`} />
            {m.label}
          </span>
        ))}
        <span className="text-zinc-400">· Cliquez une cellule pour modifier l&apos;affectation.</span>
      </div>
    </div>
  );
}

/* ── Synthesis (v10.1) ───────────────────────────────────────────────────
 * Vehicle-centric read-only summary. Lists every bus with its permanent
 * configuration (chauffeur principal · tournée principale · client · état)
 * plus any active exception for today. The single action is "Modifier le
 * véhicule" which deep-links to /admin/vehicles. The source of truth lives
 * on each vehicle's primary_* fields, not on tour_assignments.
 */
function vehicleTypeLabel(t) {
  switch ((t || "").toLowerCase()) {
    case "van":     return "Van";
    case "minibus": return "Mini-bus";
    case "bus":     return "Bus";
    default:        return t || "—";
  }
}
function SynthesisView({ vehicles, drivers, tours, assignments, loading }) {
  const today = new Date().toISOString().slice(0, 10);
  const driverById = Object.fromEntries((drivers || []).map((d) => [d.id, d]));
  const tourById = Object.fromEntries((tours || []).map((t) => [t.tour_id, t]));
  const overrideByVehicle = {};
  for (const a of assignments || []) {
    if (a.kind !== "override") continue;
    const inRange = (!a.start_date || a.start_date <= today)
                  && (!a.end_date || a.end_date >= today);
    if (!inRange) continue;
    overrideByVehicle[a.vehicle_id] = a;
  }
  return (
    <div className="space-y-3" data-testid="fa-synthesis-view">
      <div className="bg-zinc-900 text-white px-4 py-3 text-sm" data-testid="fa-synthesis-hint">
        <strong className="font-display font-black tracking-tight">Synthèse par véhicule</strong>
        <span className="text-zinc-300">
          {" "}— synthèse des <strong>affectations principales</strong>. Vous pouvez modifier l&apos;affectation
          depuis cette page (<strong>Modifier ↗</strong>) ou depuis la <em>fiche véhicule</em>&nbsp;:
          les deux interfaces utilisent <strong>la même donnée</strong> (registre d&apos;affectations).
          Pour un remplacement ponctuel, allez sur <a href="/admin/substitutions" className="underline text-[#FFC60A] font-bold">Exceptions</a>.
        </span>
      </div>
      <div className="border border-zinc-900 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 border-b border-zinc-900">
            <tr className="text-[10px] font-mono uppercase tracking-widest">
              <th className="text-left px-3 py-2">Bus</th>
              <th className="text-left px-3 py-2">Chauffeur principal</th>
              <th className="text-left px-3 py-2">Tournée principale</th>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2">État</th>
              <th className="text-left px-3 py-2">Exception aujourd&apos;hui</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {loading && vehicles.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">
                <Loader2 size={14} className="inline animate-spin mr-1" /> Chargement…
              </td></tr>
            )}
            {!loading && vehicles.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-400">
                Aucun véhicule. Ajoutez-en depuis <a href="/admin/vehicles" className="underline font-bold">Bus</a>.
              </td></tr>
            )}
            {vehicles.map((v) => {
              const drv = v.primary_driver_id && driverById[v.primary_driver_id];
              const tour = v.primary_tour_id && tourById[v.primary_tour_id];
              const exc = overrideByVehicle[v.id];
              const inWindow = (!v.primary_valid_from || v.primary_valid_from <= today)
                              && (!v.primary_valid_to || v.primary_valid_to >= today);
              const isActive = v.primary_active !== false && inWindow;
              return (
                <tr key={v.id} data-testid={`fa-synthesis-row-${v.id}`} className="hover:bg-zinc-50">
                  <td className="px-3 py-2 align-top">
                    <div className="font-bold text-base leading-tight">{v.plate || v.id?.slice(0, 6)}</div>
                    <div className="text-zinc-800 leading-tight">{v.model || "—"}</div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{vehicleTypeLabel(v.vehicle_type)}</span>
                      <span>·</span>
                      <span>{v.capacity || 0} places</span>
                      {v.navixy_tracker_id ? (
                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[9px] font-bold uppercase tracking-widest">
                          <Radio size={8} /> GPS lié
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-300 text-[9px] font-bold uppercase tracking-widest">
                          Sans GPS
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">{drv ? drv.name : <span className="text-zinc-400 italic">— Non défini —</span>}</td>
                  <td className="px-3 py-2 font-mono text-[12px]">
                    {tour ? `${tour.service_code || "?"}${tour.service_letter || ""} · ${tour.day_name || ""}` : <span className="text-zinc-400 italic">—</span>}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{v.primary_client_name || "—"}</td>
                  <td className="px-3 py-2">
                    {!v.primary_tour_id && !v.primary_driver_id ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-zinc-300 bg-zinc-50 text-[9px] font-mono font-bold uppercase tracking-widest text-zinc-500">
                        À configurer
                      </span>
                    ) : isActive ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-300 bg-emerald-100 text-[9px] font-mono font-bold uppercase tracking-widest text-emerald-800">
                        <CheckCircle2 size={9} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-300 bg-amber-100 text-[9px] font-mono font-bold uppercase tracking-widest text-amber-900">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {exc ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-purple-300 bg-purple-50 text-purple-800 font-mono">
                        🔁 {exc.driver_id ? (driverById[exc.driver_id]?.name || "chauffeur") : "exception"}
                        {" · "}{exc.start_date}{exc.end_date && exc.end_date !== exc.start_date ? ` → ${exc.end_date}` : ""}
                      </span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a
                      href={`/admin/vehicles?focus_vehicle=${v.id}`}
                      data-testid={`fa-synthesis-edit-${v.id}`}
                      title="Modifier la configuration permanente"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A] text-[10px] font-mono uppercase tracking-widest font-bold">
                      Modifier ↗
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Buses view ────────────────────────────────────────────────────────
   1 row per bus with weekly summary (count affected, replacements,
   free days). Click a tour code to edit it.
   ────────────────────────────────────────────────────────────────────── */
function BusesView({ tours, onEditCell }) {
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekStart] = useState(() => _isoMonday());

  const fetchGrid = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/fleet-assignments/grid?week_start=${weekStart}&days=5`);
      setGrid(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  useRealtime(["tour_assignments"], {
    "assignment.created":  () => fetchGrid(),
    "assignment.updated":  () => fetchGrid(),
    "assignment.deleted":  () => fetchGrid(),
    "assignment.moved":    () => fetchGrid(),
    "assignment.upserted": () => fetchGrid(),
  });

  const tourById = useMemo(() => {
    const m = {};
    for (const t of tours) m[t.tour_id] = t;
    return m;
  }, [tours]);

  if (loading && !grid) {
    return <div className="text-center py-12 text-zinc-500"><Loader2 size={18} className="inline animate-spin" /> Chargement…</div>;
  }
  const vehicles = grid?.vehicles || [];

  return (
    <div className="space-y-3" data-testid="fa-buses-view">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 font-bold">
        {vehicles.length} bus · Semaine du {_fmtFr(grid?.week_start)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {vehicles.map((v) => {
          const meta = CELL_STATE_META[v.status] || CELL_STATE_META.free;
          const dayCodes = Object.entries(v.days || {}).map(([d, c]) => ({ day: d, ...c }));
          const tourSet = [...new Set(dayCodes.filter((x) => x.tour_code && x.tour_code !== "?").map((x) => x.tour_code))];
          return (
            <div key={v.id} data-testid={`fa-bus-card-${v.id}`}
              className={`bg-white border-l-4 ${meta.border} border border-zinc-200 shadow-[2px_2px_0_0_#0A0A0B] p-3`}>
              <div className="flex items-start gap-2 mb-2">
                <Bus size={14} className="text-zinc-400 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-display font-black text-base tracking-tighter">{v.plate || v.label}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border ${meta.border} ${meta.bg} ${meta.text}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                    {v.principal_driver?.name || <span className="text-amber-700 font-bold">Aucun chauffeur</span>}
                    {v.capacity ? <span className="text-zinc-400"> · {v.capacity}+{v.pmr_seats || 0}pl</span> : null}
                  </div>
                </div>
              </div>

              {tourSet.length > 0 ? (
                <div className="flex flex-wrap gap-1 mb-2">
                  {tourSet.map((code) => {
                    const dc = dayCodes.find((x) => x.tour_code === code);
                    const tour = tourById[dc?.tour_id];
                    return (
                      <button key={code} data-testid={`fa-bus-tour-${v.id}-${code}`}
                        onClick={() => tour && onEditCell(tour, dc.state === "replacement" ? "override" : "durable", tour.assignment)}
                        className="font-display font-black text-xs tracking-tighter px-1.5 py-0.5 bg-zinc-900 text-[#FFC60A] hover:bg-[#FFC60A] hover:text-zinc-900 inline-block">
                        {code}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-zinc-50 border border-zinc-200 px-2 py-1.5 mb-2 text-[11px] text-zinc-500 font-medium flex items-center gap-1.5">
                  <Info size={11} /> Aucune tournée cette semaine
                </div>
              )}

              <div className="grid grid-cols-5 gap-1 text-[10px] font-mono text-zinc-500">
                {dayCodes.map((dc) => {
                  const dMeta = CELL_STATE_META[dc.state] || CELL_STATE_META.free;
                  return (
                    <div key={dc.day} className={`text-center px-1 py-0.5 border ${dMeta.border} ${dMeta.bg}`}>
                      <div className="uppercase font-bold text-[9px] tracking-widest text-zinc-400">{dc.day.slice(8)}</div>
                      <div className={`${dMeta.text} font-bold`}>{(dc.tour_code && dc.tour_code !== "?") ? dc.tour_code : "—"}</div>
                    </div>
                  );
                })}
              </div>

              {v.replacement_count_week > 0 && (
                <div className="mt-2 text-[10px] font-mono text-amber-700">
                  <AlertCircle size={10} className="inline" /> {v.replacement_count_week} exception(s) cette semaine
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
